/* eslint-disable */
import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Emails } from './Emails'
import { Passwords } from './Passwords'
import { DocumentTypeControl } from '@/components/forms/BasicInfo/DocumentTypeControl'
import { DocumentNumberControl } from '@/components/forms/BasicInfo/DocumentNumberControl'

import { useCardMessage, useProviderLoader } from '@/Providers'
import { useAuth } from '@/context/AuthContext'
import {
    CheckboxControl,
    InputControl,
    PhoneNumberControl,
    SelectControl
} from '@/components/form-controls'

import { Button, Image, Checkbox } from '@/components/nextui'
import { Icons } from '@/components/icons'
import { useQueryParams } from '@/hooks'
import { useUTMParams } from '@/hooks/useUTMParams'
import { useAuthApi } from '@/services/api/authApi'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { AllyOption, InscriptionQueryParams, mapSapAliadosToOptions, MessageLevel } from '@/types'
import { ErrorHandlingStrategy, SuccessStatus } from '@/types/api'
import { useFormAutosave } from '@/hooks/useFormAutosave'
import { RegisterRequest } from '@/types/auth'
import { useToast } from '@/context/ToastContext'
import { useResourcesApi } from '@/services/api/resourcesApi'



export const InscriptionProcessForm = (): JSX.Element => {
    const form = useForm<InscriptionQueryParams>({
        mode: 'onChange',
        defaultValues: {
            email: '',
            cellphone: '',
            password: '',
            idDocumentType: '',
            documentNumber: '',
            habeasData: false,
            emailConfirmation: '',
            name: '',
            lastName: '',
            hasAlly: false,
            ally: ''
        }
    })
    const { control, handleSubmit, watch, reset, trigger } = form
    const { t } = useTranslation()
    const { showCardMessage } = useCardMessage()
    const navigate = useNavigate()
    const authApi = useAuthApi({
        messageLevel: MessageLevel.ALL,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.NONE,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const { utmParams, navigateWithUTM } = useUTMParams()

    const { login: authLogin } = useAuth()
    const { setFormValues } = useQueryParams<InscriptionQueryParams>()
    const { showLoader, hideLoader } = useProviderLoader()
    const { showToast } = useToast()

    const resourcesApi = useResourcesApi(
        {
            messageLevel: MessageLevel.NONE,
            errorHandling: ErrorHandlingStrategy.RETURN
        }
    )
    const [allyOptions, setAllyOptions] = useState<AllyOption[]>([])

    useEffect(() => {
        (async () => {
            try {
                const res = await resourcesApi.getAliados() // ApiResponse<SapAliado[]>
                setAllyOptions(mapSapAliadosToOptions(res.data ?? []))
            } catch (e) {
                setAllyOptions([]) // fallback silencioso o muestra toast si prefieres
            }
        })()
    }, [])

    // Autosave del formulario (persistencia en localStorage)
    const storageKey = `draft:create-account:${Date.now()}`
    const { clearDraft } = useFormAutosave(form, storageKey)

    const password = watch('password')
    const email = watch('email')
    const watchDocumentType = watch('idDocumentType')
    const watchHasAlly = watch('hasAlly')

    // Revalidar confirmación de email cuando cambia el email principal
    useEffect(() => {
        if (watch('emailConfirmation')) {
            trigger('emailConfirmation')
        }
    }, [email, trigger, watch])

    const onSubmit = async (data: InscriptionQueryParams): Promise<void> => {
        try {
            showLoader()
            // Mapear solo los campos requeridos por RegisterRequest
            const aliadoId = data.hasAlly ? Number(data.ally) : null

            // Mapear solo los campos requeridos por RegisterRequest
            const payload: RegisterRequest = {
                idDocumentType: data.idDocumentType,
                documentNumber: data.documentNumber,
                name: data.name || '',
                lastName: data.lastName || '',
                cellphone: data.cellphone,
                email: data.email,
                emailConfirmation: data.emailConfirmation || data.email,
                password: data.password,
                confirmPassword: data.confirmPassword || data.password,
                habeasData: data.habeasData,
                aliadoId, // <- único campo de aliado que enviamos a auth
                // Se envían todas las UTM que recibamos (incluyendo las vacías en la URL)
                ...utmParams
            }





            const response = await authApi.register(payload) // sin inscriptionsApi

            if (SuccessStatus.ANY_SUCCESS(response.status)) {
                // 1) Obtén el id del aspirante y de la inscripción del response
                const idAspirant = response.data?.idAspirant
                const idInscription = response.data?.idInscription

                // 2) Guardar parámetros UTM si existen
                console.log('UTM DEBUG - idAspirant:', idAspirant)
                console.log('UTM DEBUG - idInscription:', idInscription)
                console.log('UTM DEBUG - utmParams:', utmParams)

                if (
                    idAspirant &&
                    utmParams &&
                    Object.keys(utmParams).length > 0
                ) {
                    const utmList = Object.entries(utmParams).map(
                        ([key, value]) => ({
                            utmCode: key,
                            utmValue: String(value)
                        })
                    )

                    console.log('UTM DEBUG - Sending to API:', utmList)

                    const utmResponse = await aspirantsApi.saveUTM({
                        aspirantId: idAspirant,
                        inscriptionId: idInscription || 0,
                        utms: utmList
                    })

                    console.log('UTM DEBUG - API Response:', utmResponse)
                } else {
                    console.log('UTM DEBUG - POST not sent. Reason:', {
                        noIdAspirant: !idAspirant,
                        noUtmParams:
                            !utmParams || Object.keys(utmParams).length === 0
                    })
                }

                showToast({
                    type: 'success',
                    message:
                        '¡Registro exitoso! Revisa tu correo electrónico para confirmar tu cuenta.'
                })
                clearDraft()
                navigateWithUTM(
                    `/verify-email?email=${encodeURIComponent(data.email)}`
                )

            } else {
                // Mostrar el mensaje específico de la respuesta
                let errorMessage =
                    'No fue posible completar el registro. Por favor, revisa los datos e intenta nuevamente.'

                if (response.data?.message) {
                    if (Array.isArray(response.data.message)) {
                        errorMessage =
                            response.data.message[0]?.messageDesc ||
                            errorMessage
                    } else {
                        errorMessage = response.data.message
                    }
                } else if (response.message) {
                    if (Array.isArray(response.message)) {
                        errorMessage =
                            response.message[0]?.messageDesc || errorMessage
                    } else {
                        errorMessage = response.message
                    }
                }

                showToast({
                    type: 'error',
                    message: errorMessage
                })
            }
        } catch (e: any) {
            console.error('Registration error:', e)

            // Mejorar el manejo de mensajes de error
            let errorMessage = 'Ocurrió un error al registrar tu cuenta.'

            if (e?.response?.data?.message) {
                if (Array.isArray(e.response.data.message)) {
                    errorMessage =
                        e.response.data.message[0]?.messageDesc || errorMessage
                } else {
                    errorMessage = e.response.data.message
                }
            } else if (e?.message) {
                errorMessage = e.message
            }

            showToast({
                type: 'error',
                message: errorMessage
            })
        } finally {
            hideLoader()
        }
    }

    return (
        <article className="flex flex-col justify-center items-center p-4 min-h-screen bg-gradient-ean-2 dark:bg-gradient-ean">
            <motion.form
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 2, ease: 'easeOut' }}
                onSubmit={handleSubmit(onSubmit)}
                className="p-6 space-y-6 w-full max-w-md text-center bg-white rounded-lg border shadow-lg border-default-400 dark:bg-secondary dark:border-default-700"
            >
                <div className="flex flex-col gap-4 items-center">
                    <Image
                        src="/LogoEAN.jpg"
                        alt="logo"
                        className="w-[130px]"
                    />
                </div>

                <DocumentTypeControl
                    control={control}
                    isDisabled={false}
                />

                <DocumentNumberControl
                    control={control}
                    watchDocumentType={watchDocumentType}
                    text="basicDataInformation"
                    isDisabled={false}
                />

                <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
                    <Icons.Info className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <p className="text-xs text-left text-amber-800 dark:text-amber-300 leading-snug">
                        Registra tu nombre y apellidos <span className="font-semibold">exactamente como aparecen en tu documento de identidad</span>, sin abreviaturas ni cambios.
                    </p>
                </div>

                <InputControl
                    name="name"
                    forceUppercase
                    control={control}
                    label={t('forms.common.name')}
                    rules={{
                        required: t('forms.common.required.nameRequired')
                    }}
                />

                <InputControl
                    name="lastName"
                    forceUppercase
                    control={control}
                    label={t('forms.common.lastName')}
                    rules={{
                        required: t('forms.common.required.lastNameRequired')
                    }}
                />

<CheckboxControl
                    name="hasAlly"
                    control={control}
                    label={t('createAccount.hasAlly')}
                />

                {watchHasAlly && (
                    <SelectControl
                        name="ally"
                        control={control}
                        label={t('createAccount.ally')}
                        options={allyOptions}
                        rules={{
                            required: watchHasAlly
                                ? t('createAccount.required.allyRequired')
                                : false
                        }}
                    />
                )}

                <PhoneNumberControl
                    name="cellphone"
                    control={control}
                    label={t('forms.common.cellphoneNumber')}
                    rules={{
                        required: t('forms.common.cellphoneNumber'),
                        validate: (value: string) => {
                            if (!value) return true
                            if (value.startsWith('+57')) {
                                return /^\+573\d{9}$/.test(value)
                                    ? true
                                    : t('forms.common.invalidPhoneNumber')
                            }
                            return true
                        }
                    }}
                />

                <Emails
                    control={control}
                    email={email}
                />
                <Passwords
                    control={control}
                    password={password}
                />

                <div className="flex items-start gap-2">
                    <Controller
                        name="habeasData"
                        control={control}
                        rules={{
                            required: t(
                                'createAccount.required.habeasDataRequired'
                            )
                        }}
                        defaultValue={false}
                        render={({
                            field: { onChange, value },
                            fieldState: { error }
                        }) => (
                            <div className="flex flex-col gap-1 w-full">
                                <div className="flex items-start gap-2">
                                    <Checkbox
                                        isSelected={value}
                                        onValueChange={onChange}
                                        isInvalid={!!error}
                                        className="mt-0.5"
                                    />
                                    <div className="text-[10px] leading-tight text-gray-600 flex-1 text-justify">
                                        De manera voluntaria, explícita,
                                        informada e inequívoca,{' '}
                                        <a
                                            href="https://universidadean.edu.co/la-universidad/quienes-somos/orientacion-estrategica/reglamentos-universidad-ean/autorizacion-para-uso-de-datos-personales"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                        >
                                            autorizo a la Universidad Ean para
                                            tratar mis datos personales
                                        </a>
                                        , de acuerdo con lo dispuesto en los
                                        artículos 5, 7 y concordantes del
                                        Decreto 1377 de 2013 y las demás
                                        disposiciones legales referentes al
                                        tema. Para mayor información acerca del
                                        manejo de tus datos personales, puedes
                                        revisar nuestro{' '}
                                        <a
                                            href="https://universidadean.edu.co/la-universidad/quienes-somos/orientacion-estrategica/reglamentos-universidad-ean/aviso-de-privacidad"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                        >
                                            Aviso de privacidad
                                        </a>{' '}
                                        y nuestra{' '}
                                        <a
                                            href="https://universidadean.edu.co/sites/default/files/institucion/acuerdos/politica-tratamiento-de-datos-personales.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                        >
                                            Política de tratamiento de datos
                                            personales
                                        </a>
                                        .
                                    </div>
                                </div>
                                {error && (
                                    <span className="text-xs text-danger">
                                        {error.message}
                                    </span>
                                )}
                            </div>
                        )}
                    />
                </div>

                <div className="flex flex-col gap-y-4">
                    <Button
                        type="submit"
                        className="w-full text-white bg-primary hover:bg-primary"
                    >
                        {t('createAccount.title')}
                    </Button>
                    <span className="flex gap-x-2 justify-center">
                        {t('createAccount.yesAccount')}
                        <Link
                            to="/"
                            className="cursor-pointer text-primary"
                        >
                            {t('createAccount.loginMessage')}
                        </Link>
                    </span>
                </div>
            </motion.form>
        </article>
    )
}
